import React, { useState, useMemo, useEffect } from 'react';
import { FileText, DollarSign, Clock, Package, User, Calendar, Search, Filter, ChevronDown, ChevronRight, ArrowLeft, Activity } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigation } from '../contexts/NavigationContext';
import { useTeachingMode } from '../contexts/TeachingModeContext';
import SalesCycleDashboard from './salesOrder/SalesCycleDashboard';
import EnhancedTeachingAids from './EnhancedTeachingAids';

const SalesOrderView = () => {
  // State management
  const [activeView, setActiveView] = useState('dashboard'); // dashboard, list, detail, sales-cycle
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('order_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const { goBack, cameFrom } = useNavigation();
  const { showTeachingAids, currentWorksheet } = useTeachingMode();

  // Fetch sales orders from Supabase
  useEffect(() => {
    fetchSalesOrders();
    
    // Check for converted sales order from quotation
    const convertedSalesOrder = localStorage.getItem('currentSalesOrder');
    if (convertedSalesOrder) {
      try {
        const salesOrder = JSON.parse(convertedSalesOrder);
        setSelectedOrder(salesOrder);
        setActiveView('detail');
        // Clear the localStorage after using it
        localStorage.removeItem('currentSalesOrder');
      } catch (error) {
        console.error('Error parsing converted sales order:', error);
      }
    }
  }, []);

  const fetchSalesOrders = async () => {
    try {
      setLoading(true);
      
      // Fetch sales orders
      const { data: ordersData, error: ordersError } = await supabase
        .from('sales_orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      // Fetch sales order items for each order
      const ordersWithItems = await Promise.all(
        ordersData.map(async (order) => {
          const { data: itemsData, error: itemsError } = await supabase
            .from('sales_order_items')
            .select('*')
            .eq('sales_order_id', order.order_id);

          if (itemsError) throw itemsError;

          return {
            ...order,
            items: itemsData || []
          };
        })
      );

      setSalesOrders(ordersWithItems);
    } catch (error) {
      console.error('Error fetching sales orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Order status options
  const orderStatuses = [
    {
      status_id: 'draft',
      status_name: '草稿',
      color_code: '#6B7280', // Gray
      icon: 'FileText'
    },
    {
      status_id: 'processing',
      status_name: '處理中',
      color_code: '#3B82F6', // Blue
      icon: 'Clock'
    },
    {
      status_id: 'shipped',
      status_name: '已出貨',
      color_code: '#10B981', // Green
      icon: 'Truck'
    },
    {
      status_id: 'delivered',
      status_name: '已送達',
      color_code: '#6366F1', // Indigo
      icon: 'CheckCircle'
    },
    {
      status_id: 'completed',
      status_name: '已完成',
      color_code: '#059669', // Emerald
      icon: 'Check'
    },
    {
      status_id: 'cancelled',
      status_name: '已取消',
      color_code: '#EF4444', // Red
      icon: 'X'
    }
  ];

  // Helper functions
  const getStatusDetails = (statusId) => {
    return orderStatuses.find(status => status.status_id === statusId) || {
      status_name: '未知',
      color_code: '#6B7280',
      icon: 'HelpCircle'
    };
  };

  // Filtering and sorting
  const filteredOrders = useMemo(() => {
    return salesOrders.filter(order => {
      // Apply status filter
      if (statusFilter !== 'all' && order.status !== statusFilter) {
        return false;
      }
      
      // Apply search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          order.order_number?.toLowerCase().includes(searchLower) ||
          order.customer_name?.toLowerCase().includes(searchLower) ||
          order.sales_rep_id?.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    });
  }, [salesOrders, statusFilter, searchTerm]);

  const sortedOrders = useMemo(() => {
    return [...filteredOrders].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'order_number':
          comparison = (a.order_number || '').localeCompare(b.order_number || '');
          break;
        case 'customer_name':
          comparison = (a.customer_name || '').localeCompare(b.customer_name || '');
          break;
        case 'order_date':
          comparison = new Date(a.order_date || 0) - new Date(b.order_date || 0);
          break;
        case 'delivery_date':
          comparison = new Date(a.delivery_date || 0) - new Date(b.delivery_date || 0);
          break;
        case 'total_amount':
          comparison = (a.total_amount || 0) - (b.total_amount || 0);
          break;
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredOrders, sortField, sortDirection]);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setActiveView('detail');
  };

  const handleNavigateToModule = (moduleId) => {
    // This will be handled by the parent component to navigate to different modules
    console.log(`Navigate to module: ${moduleId}`);
  };

  const renderWorkflowBanner = () => {
    if (cameFrom('workflows')) {
      return (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-800">來自業務流程: 銷售週期管理</h3>
                <p className="text-sm text-purple-600">階段C: 商務談判與銷售訂單確認 - 銷售訂單</p>
              </div>
            </div>
            <button
              onClick={goBack}
              className="flex items-center space-x-2 bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回銷售週期</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  // Dashboard statistics
  const totalOrders = salesOrders.length;
  const processingOrders = salesOrders.filter(o => o.status === 'processing').length;
  const shippedOrders = salesOrders.filter(o => o.status === 'shipped').length;
  const totalValue = salesOrders.reduce((sum, o) => sum + (parseFloat(o.total_amount) || 0), 0);

  // If sales-cycle view is active, render the SalesCycleDashboard
  if (activeView === 'sales-cycle') {
    return (
      <SalesCycleDashboard 
        onBack={() => setActiveView('dashboard')}
        onNavigate={handleNavigateToModule}
        navigationSource="sales-orders"
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading sales orders...</div>
      </div>
    );
  }

  // Render dashboard view
  const renderDashboard = () => (
    <div className="space-y-6">
      {renderWorkflowBanner()}
      
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">銷售訂單儀表板</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setActiveView('sales-cycle')}
            className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Activity className="w-4 h-4" />
            <span>銷售週期管理</span>
          </button>
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FileText className="w-4 h-4" />
            <span>查看所有訂單</span>
          </button>
        </div>
      </div>
      
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">總訂單數</p>
              <p className="text-2xl font-bold text-gray-800">{totalOrders}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">處理中訂單</p>
              <p className="text-2xl font-bold text-gray-800">{processingOrders}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">已出貨訂單</p>
              <p className="text-2xl font-bold text-gray-800">{shippedOrders}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <Package className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">總訂單金額</p>
              <p className="text-2xl font-bold text-gray-800">
                ${totalValue.toLocaleString()}
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* 銷售週期快速入口 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">銷售週期快速入口</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <button
            onClick={() => setActiveView('sales-cycle')}
            className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
          >
            <div className="bg-blue-100 p-3 rounded-full mb-3">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-gray-800">潛客開發</span>
            <span className="text-xs text-gray-500">A階段</span>
          </button>

          <button
            onClick={() => setActiveView('sales-cycle')}
            className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-300 transition-colors"
          >
            <div className="bg-green-100 p-3 rounded-full mb-3">
              <FileText className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-sm font-medium text-gray-800">報價提案</span>
            <span className="text-xs text-gray-500">B階段</span>
          </button>

          <button
            onClick={() => setActiveView('sales-cycle')}
            className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors"
          >
            <div className="bg-purple-100 p-3 rounded-full mb-3">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <span className="text-sm font-medium text-gray-800">商務談判</span>
            <span className="text-xs text-gray-500">C階段</span>
          </button>

          <button
            onClick={() => setActiveView('sales-cycle')}
            className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-orange-50 hover:border-orange-300 transition-colors"
          >
            <div className="bg-orange-100 p-3 rounded-full mb-3">
              <Package className="w-6 h-6 text-orange-600" />
            </div>
            <span className="text-sm font-medium text-gray-800">履約交付</span>
            <span className="text-xs text-gray-500">D階段</span>
          </button>

          <button
            onClick={() => setActiveView('sales-cycle')}
            className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors"
          >
            <div className="bg-red-100 p-3 rounded-full mb-3">
              <Calendar className="w-6 h-6 text-red-600" />
            </div>
            <span className="text-sm font-medium text-gray-800">收款結算</span>
            <span className="text-xs text-gray-500">E階段</span>
          </button>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">最新訂單</h3>
            <button
              onClick={() => setActiveView('list')}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              查看全部
            </button>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {salesOrders.slice(0, 5).map((order) => (
              <div
                key={order.order_id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                onClick={() => handleViewOrder(order)}
              >
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{order.order_number}</h4>
                    <p className="text-sm text-gray-600">{order.customer_name}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="font-semibold text-gray-800">
                      ${(order.total_amount || 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600">{order.order_date}</p>
                  </div>
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: getStatusDetails(order.status).color_code }}
                  >
                    {getStatusDetails(order.status).status_name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Render list view
  const renderList = () => (
    <div className="space-y-6">
      {renderWorkflowBanner()}
      
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">銷售訂單列表</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setActiveView('sales-cycle')}
            className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Activity className="w-4 h-4" />
            <span>銷售週期管理</span>
          </button>
          <button
            onClick={() => setActiveView('dashboard')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回儀表板</span>
          </button>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="搜尋訂單..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有狀態</option>
            {orderStatuses.map((status) => (
              <option key={status.status_id} value={status.status_id}>
                {status.status_name}
              </option>
            ))}
          </select>
          
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="order_date">訂單日期</option>
            <option value="order_number">訂單編號</option>
            <option value="customer_name">客戶名稱</option>
            <option value="total_amount">訂單金額</option>
            <option value="delivery_date">交貨日期</option>
            <option value="status">狀態</option>
          </select>
          
          <button
            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {sortDirection === 'asc' ? '升序' : '降序'}
            <ChevronDown className={`w-4 h-4 ml-2 transform ${sortDirection === 'asc' ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('order_number')}
              >
                訂單編號
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('customer_name')}
              >
                客戶名稱
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('order_date')}
              >
                訂單日期
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('delivery_date')}
              >
                交貨日期
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('total_amount')}
              >
                訂單金額
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('status')}
              >
                狀態
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedOrders.map((order) => (
              <tr key={order.order_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {order.order_number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{order.customer_name}</div>
                  <div className="text-sm text-gray-500">{order.sales_rep_id}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {order.order_date}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {order.delivery_date}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ${(order.total_amount || 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span 
                    className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full text-white"
                    style={{ backgroundColor: getStatusDetails(order.status).color_code }}
                  >
                    {getStatusDetails(order.status).status_name}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleViewOrder(order)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    查看詳情
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDetail = () => {
    if (!selectedOrder) return null;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        {/* Conversion Success Banner */}
        {selectedOrder.quotation_reference && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="bg-green-100 p-2 rounded-full">
                <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-900">
                  銷售訂單已成功建立
                </h3>
                <p className="text-sm text-green-700">
                  此銷售訂單由報價單 {selectedOrder.quotation_reference} 轉換而來
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">訂單詳情 - {selectedOrder.sales_order_id || selectedOrder.order_number}</h2>
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回列表</span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Order Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">訂單資訊</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">訂單編號:</span>
                <span className="font-medium">{selectedOrder.sales_order_id || selectedOrder.order_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">訂單日期:</span>
                <span className="font-medium">{selectedOrder.order_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">交貨日期:</span>
                <span className="font-medium">{selectedOrder.expected_delivery_date || selectedOrder.delivery_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">付款條件:</span>
                <span className="font-medium">{selectedOrder.payment_terms}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">狀態:</span>
                <span 
                  className="px-2 py-1 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: getStatusDetails(selectedOrder.status).color_code }}
                >
                  {getStatusDetails(selectedOrder.status).status_name}
                </span>
              </div>
              {selectedOrder.quotation_reference && (
                <div className="flex justify-between">
                  <span className="text-gray-600">報價單參考:</span>
                  <span className="font-medium text-blue-600">{selectedOrder.quotation_reference}</span>
                </div>
              )}
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">客戶資訊</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">客戶名稱:</span>
                <span className="font-medium">{selectedOrder.customer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">聯絡人:</span>
                <span className="font-medium">{selectedOrder.contact_person}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">電郵:</span>
                <span className="font-medium">{selectedOrder.contact_email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">電話:</span>
                <span className="font-medium">{selectedOrder.contact_phone}</span>
              </div>
              {selectedOrder.sales_rep_id && (
                <div className="flex justify-between">
                  <span className="text-gray-600">銷售代表:</span>
                  <span className="font-medium">{selectedOrder.sales_rep_id}</span>
                </div>
              )}
              {selectedOrder.shipping_address && (
                <div className="flex justify-between">
                  <span className="text-gray-600">送貨地址:</span>
                  <span className="font-medium">{selectedOrder.shipping_address}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">訂單項目</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    產品名稱
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    數量
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    單價
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    小計
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {selectedOrder.items.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.product_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.quantity} {item.unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${(item.unit_price || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${(item.subtotal || item.line_total || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="3" className="px-6 py-4 text-sm font-medium text-gray-900 text-right">
                    小計:
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-gray-900">
                    ${(selectedOrder.subtotal || 0).toLocaleString()}
                  </td>
                </tr>
                {selectedOrder.tax_amount > 0 && (
                  <tr>
                    <td colSpan="3" className="px-6 py-4 text-sm font-medium text-gray-900 text-right">
                      稅額:
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-gray-900">
                      ${(selectedOrder.tax_amount || 0).toLocaleString()}
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan="3" className="px-6 py-4 text-sm font-medium text-gray-900 text-right">
                    總計:
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-gray-900">
                    ${(selectedOrder.total_amount || 0).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderList();
      case 'detail':
        return renderDetail();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {renderContent()}
      </div>
      
      {/* Teaching Aids Panel */}
      {showTeachingAids && (
        <EnhancedTeachingAids 
          module="salesOrder" 
          isVisible={true}
        />
      )}
    </div>
  );
};

export default SalesOrderView;
